import { useState, useEffect, useRef } from "react";
import { CircularProgress, LinearProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GeneralButton from "components/Elements/GeneralButton";
import ListTable from "./ListTable";
import DeleteDialog from "components/Elements/DeleteDialog";
import {
	duplicateQuestions,
	pasteModelTaskQuestion,
	pasteModelTaskQuestions,
	repositionQuestions,
} from "services/models/modelDetails/modelTasks/questions";
import AddEditModel from "./AddEditModel";
import { connect } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import { appPath, modelServiceLayout, modelsPath } from "helpers/routePaths";
import DetailsPanel from "components/Elements/DetailsPanel";
import withMount from "components/HOC/withMount";
import { makeStyles } from "tss-react/mui";
import useModelTaskQuestions from "hooks/ModuleTaskTableHooks/useModalTaskQuestions";

// Styling Task Question
const useStyles = makeStyles()((theme) => ({
	question: { display: "flex", flexDirection: "column" },
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	pasteTask: { background: "#ED8738" },
	buttons: { display: "flex", justifyContent: "space-around" },
}));

const Questions = ({
	captions,
	taskInfo,
	getError,
	access,
	isMounted,
	service,
	allowConditionMonitoringDataAlert,
}) => {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const scrollRef = useRef();
	const navigate = useNavigate();
	const [questionId, setQuestionId] = useState(null);
	const [model, setModel] = useState({
		// delete: false,
		// addEdit: false,
		// copy: false,
	});
	const [addEditType, setAddEditType] = useState("add");
	const [isReadOnly, setIsReadOnly] = useState(false);

	// hooks
	const {
		loading,
		setLoading,
		fetchTaskQuestion,
		setOriginalList,
		originalList,
		setData,
		data,
		id,
		errorResponse,
		CtxDispatch,
		state,
		MtxDispatch,
	} = useModelTaskQuestions({
		taskInfo,
		getError,
		isMounted,
	});
	useEffect(() => {
		setModel({
			delete: false,
			addEdit: false,
			copy:
				state.isQuestionTaskDisabled && !localStorage.getItem("taskquestion"),
			copyPlural:
				state.isQuestionsDisabled && !localStorage.getItem("tasksquestions"),
		});
	}, [state]);

	// HANDLE ADD AND EDIT IN MODEL
	const handleEdit = (id) => {
		setQuestionId(id);
		setModel((th) => ({ ...th, addEdit: true }));
		setAddEditType("edit");
		setIsReadOnly(false);
	};

	// HANDLE VIEW (read-only) - used on published models
	const handleView = (id) => {
		setQuestionId(id);
		setModel((th) => ({ ...th, addEdit: true }));
		setAddEditType("edit");
		setIsReadOnly(true);
	};

	const handleAddEditClose = () => {
		setModel((th) => ({ ...th, addEdit: false }));
		setQuestionId(null);
	};

	const handleAddEditComplete = () => {
		fetchTaskQuestion();
		handleAddEditClose();
	};

	// HANDLE DUPLICATE
	const handleDuplicate = async (id) => {
		setLoading((th) => ({ ...th, loader: true }));
		try {
			let result = await duplicateQuestions(id);

			setLoading((th) => ({ ...th, loader: false }));

			if (result.status) {
				await fetchTaskQuestion();
				scrollRef.current.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
					inline: "start",
				});
			} else {
				errorResponse(result);
			}
		} catch (e) {
			return;
		}
	};

	// HANDLE COPY AND PASTE
	const handleCopy = (id) => {
		setQuestionId(id);
		localStorage.setItem("taskquestion", id);
		setModel((th) => ({ ...th, copy: false }));
		MtxDispatch({ type: "DISABLE_QUESTION_TASK", payload: false });
	};

	const handlePaste = async () => {
		setLoading((th) => ({ ...th, loader: true }));
		try {
			const taskQuestionId = localStorage.getItem("taskquestion");
			let result = await pasteModelTaskQuestion(taskInfo.id, {
				modelVersionTaskQuestionID: taskQuestionId,
			});

			if (!isMounted.aborted) {
				setLoading((th) => ({ ...th, loader: false }));
				if (result.status) {
					fetchTaskQuestion();
					setQuestionId(null);
				} else {
					errorResponse(result);
				}
			}
		} catch (e) {
			return;
		}
	};

	const handlePasteQuestions = async () => {
		setLoading((th) => ({ ...th, loader: true }));
		try {
			const taskQuestionsId = localStorage.getItem("tasksquestions");
			let result = await pasteModelTaskQuestions(taskInfo.id, {
				ModelVersionTaskID: taskQuestionsId,
			});

			if (!isMounted.aborted) {
				setLoading((th) => ({ ...th, loader: false }));
				if (result.status) {
					fetchTaskQuestion();
					setQuestionId(null);
				} else {
					errorResponse(result);
				}
			}
		} catch (e) {
			return;
		}
	};

	const checkcopyQuestionStatus = async () => {
		try {
			const questionTaskId = localStorage.getItem("taskquestion");

			if (questionTaskId) {
				setModel((th) => ({ ...th, copy: false }));
			}
		} catch (error) {
			return;
		}
	};

	const checkcopyQuestionsStatus = async () => {
		try {
			const questionTaskId = localStorage.getItem("tasksquestions");
			if (questionTaskId) {
				setModel((th) => ({ ...th, copyPlural: false }));
			}
		} catch (error) {
			return;
		}
	};

	const visibilitychangeCheck = function () {
		if (!document.hidden) {
			checkcopyQuestionStatus();
			checkcopyQuestionsStatus();
		}
	};

	useEffect(() => {
		checkcopyQuestionStatus();
		checkcopyQuestionsStatus();
		document.addEventListener("visibilitychange", visibilitychangeCheck);
		return () =>
			document.removeEventListener("visibilitychange", visibilitychangeCheck);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// HANDLE SERVICE LAYOUT
	const handleServiceLayout = (modelVersionTaskQuestionID) => {
		navigate(`${appPath}${modelsPath}/${id}${modelServiceLayout}`, {
			state: { modelVersionTaskQuestionID },
		});
	};

	// HANDLE DELETE OPERATIONS
	const handleDelete = (id) => {
		setQuestionId(id);
		setModel((th) => ({ ...th, delete: true }));
	};

	const closeDeleteDialog = () => {
		setQuestionId(null);
		setModel((th) => ({ ...th, delete: false }));
	};

	const handleRemoveData = (id) => {
		const filtered = data.filter((x) => x.id !== id);
		setData(filtered);
		CtxDispatch({
			type: "TAB_COUNT",
			payload: {
				countTab: "questionCount",
				data: filtered?.length,
			},
		});
		if (filtered.length > 0) {
			CtxDispatch({
				type: "SET_QUESTIONS",
				payload: true,
			});
		} else {
			CtxDispatch({
				type: "SET_QUESTIONS",
				payload: false,
			});
		}
	};

	// HANDLE DRAG AND DROP

	const handleDragEnd = async (e) => {
		if (!e.destination) {
			return;
		}

		if (e.destination.index === e.source.index) return;

		const result = [...data];
		const [removed] = result.splice(e.source.index, 1);
		result.splice(e.destination.index, 0, removed);
		setData(result);
		try {
			let payloadBody = {
				id: e.draggableId,
				destinationId: e?.destination?.destinationId,
				direction: e?.destination?.direction,
			};
			const response = await repositionQuestions(e.draggableId, payloadBody);
			if (!isMounted.aborted) {
				if (response.status) {
					const newDate = result.map((x, i) =>
						i === e.destination.index ? { ...x, pos: response.data.pos } : x
					);
					setOriginalList(newDate);
					setData(newDate);
				} else {
					setData(originalList);
				}
			}
		} catch (error) {
			setData(originalList);
		}
	};

	if (loading.fetch) {
		return <CircularProgress />;
	}

	return (
		<>
			{model.addEdit && (
				<AddEditModel
					open={model.addEdit}
					questionDetail={data.find((x) => x.id === questionId)}
					title={captions.singular}
					handleClose={handleAddEditClose}
					taskId={taskInfo.id}
					handleAddEditComplete={handleAddEditComplete}
					getError={getError}
					addEditType={addEditType}
					isReadOnly={isReadOnly}
					access={access}
					allowConditionMonitoringDataAlert={allowConditionMonitoringDataAlert}
				/>
			)}
			<DeleteDialog
				open={model.delete}
				entityName={`${captions?.taskCaption} ${captions.singular}`}
				deleteEndpoint={"/api/modelversiontaskquestions"}
				deleteID={questionId}
				closeHandler={closeDeleteDialog}
				handleRemoveData={handleRemoveData}
			/>
			<div className={classes.question}>
				<div className={classes.header}>
					<DetailsPanel header={captions.plural} dataCount={data.length} />
					{access === "F" && !state.modelDetail?.isPublished ? (
						<span className={classes.buttons}>
							<GeneralButton
								className={classes.pasteTask}
								onClick={handlePasteQuestions}
								disabled={model.copyPlural}
							>
								{t("action.pasteEntity", { entity: captions.plural })}
							</GeneralButton>
							<GeneralButton
								className={classes.pasteTask}
								onClick={handlePaste}
								disabled={model.copy}
							>
								{t("action.pasteEntity", { entity: captions.singular })}
							</GeneralButton>
							<GeneralButton
								onClick={() => {
									setModel((th) => ({ ...th, addEdit: true }));
									setAddEditType("add");
									setIsReadOnly(false);
								}}
							>
								{t("action.addNew")}
							</GeneralButton>
						</span>
					) : null}
				</div>
				{loading.loader ? <LinearProgress /> : null}
				<ListTable
					service={service}
					data={data}
					handleDelete={handleDelete}
					handleEdit={handleEdit}
					handleView={handleView}
					handleDuplicate={handleDuplicate}
					handleCopy={handleCopy}
					handleServiceLayout={handleServiceLayout}
					handleDragEnd={handleDragEnd}
					access={access}
					disable={state.modelDetail?.isPublished}
					allowConditionMonitoringDataAlert={allowConditionMonitoringDataAlert}
				/>
				<div ref={scrollRef} />
			</div>
		</>
	);
};

const mapDispatchToProps = (dispatch) => ({
	getError: (msg) => dispatch(showError(msg)),
});

export default connect(null, mapDispatchToProps)(withMount(Questions));
